import { useMemo } from 'react';

import {
  type GetPayV2TopGeosQuery,
  PAY_V2_TOPGEOS_QUERY,
} from '@revelio/data-access';
import { NoData } from '@revelio/replots';

import { useSelectionListState } from '../../../../../shared/selection-lists/hooks/useSelectionListState';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { getCompensationV2Filters } from '../../../filters/utils/getCompensationFilters';
import { usePlotMetric } from '../../../hooks/usePlotMetric';
import { useChart } from '../../Chart/ChartProvider';
import type { MapPoint } from '../../Chart/Map/types';
import { determineLocationType } from '../../Chart/Map/utils/location-type-utils';
import {
  isPayMetric,
  type PayMetricValues,
} from '../../PageConfigProvider/types/metric';
import type { DataProviderChildren } from '../types';
import {
  extractGranularitiesWithIds,
  useCoordinateResolverForMixedGranularities,
} from '../utils/coordinate-resolver';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from '../utils/data-provider-results-utils';

type CompensationMapDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  exchangeRate?: number;
  children: DataProviderChildren<{ points: MapPoint[] } | undefined>;
};

// There are currently a bit more than 1000 geography entities.
// Going forward, we could either increase of make multiple queries to get data for each zoom level.
const MAX_TOP_GEOS_ENTITIES = 1024;

export const CompensationMapDataProvider = ({
  primaryDimension,
  exchangeRate,
  children,
}: CompensationMapDataProviderProps) => {
  const { item: _item } = useChart();
  const metric = usePlotMetric();
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { selectionLists } = useSelectionListState();
  const compensationFilters = getCompensationV2Filters({
    primaryDimension,
    effectiveFilters,
  });

  // Create filter configurations for dual requests when regions are selected
  const { regionFilters, countryFilters, needsDualRequest } = useMemo(() => {
    if (!selectionLists) {
      return {
        regionFilters: {
          ...compensationFilters,
          options: {
            required_top_geos_length: MAX_TOP_GEOS_ENTITIES,
          },
        },
        countryFilters: null,
        needsDualRequest: false,
      };
    }

    const { region, country, msa } = compensationFilters.filters;

    // If no geo filters are selected, use all regions
    if (!region?.length && !country?.length && !msa?.length) {
      const enrichedRegion =
        selectionLists.region?.map((r) => Number(r.id)) || [];
      return {
        regionFilters: {
          ...compensationFilters,
          filters: {
            ...compensationFilters.filters,
            region: enrichedRegion,
            country: [],
            msa: [],
          },
          options: {
            required_top_geos_length: MAX_TOP_GEOS_ENTITIES,
          },
        },
        countryFilters: null,
        needsDualRequest: false,
      };
    }
    // If only regions are selected, we need dual requests
    else if (region?.length && !country?.length && !msa?.length) {
      const enrichedCountry =
        selectionLists.country
          ?.filter((c) => region.includes(Number(c.region)))
          .map((c) => Number(c.id)) || [];

      return {
        regionFilters: {
          ...compensationFilters,
          filters: {
            ...compensationFilters.filters,
            region: region,
            country: [],
            msa: [],
          },
          options: {
            required_top_geos_length: MAX_TOP_GEOS_ENTITIES,
          },
        },
        countryFilters: {
          ...compensationFilters,
          filters: {
            ...compensationFilters.filters,
            region: [],
            country: enrichedCountry,
            msa: [],
          },
          options: {
            required_top_geos_length: MAX_TOP_GEOS_ENTITIES,
          },
        },
        needsDualRequest: true,
      };
    }
    // For other cases (countries or MSAs selected), use single request
    else {
      return {
        regionFilters: {
          ...compensationFilters,
          options: {
            required_top_geos_length: MAX_TOP_GEOS_ENTITIES,
          },
        },
        countryFilters: null,
        needsDualRequest: false,
      };
    }
  }, [compensationFilters, selectionLists]);

  // First request (always made)
  const [{ data: regionData, status: regionStatus }] =
    useUrqlQueryWithStatus<GetPayV2TopGeosQuery>({
      query: PAY_V2_TOPGEOS_QUERY,
      variables: {
        req: regionFilters,
      },
    });

  // Second request (only when dual request is needed)
  const [{ data: countryData, status: countryStatus }] =
    useUrqlQueryWithStatus<GetPayV2TopGeosQuery>({
      query: PAY_V2_TOPGEOS_QUERY,
      variables: {
        req: countryFilters,
      },
      pause: !needsDualRequest || !countryFilters,
    });

  // Combine status and data from both requests
  const status = needsDualRequest
    ? combineQueryStatus([regionStatus, countryStatus])
    : regionStatus;

  const data = useMemo(() => {
    if (!needsDualRequest) {
      return regionData;
    }

    // Merge data from both requests
    if (!regionData?.pay_v2?.entities || !countryData?.pay_v2?.entities) {
      return regionData || countryData;
    }

    // Combine entities from both requests
    const combinedEntities = [
      ...regionData.pay_v2.entities,
      ...countryData.pay_v2.entities,
    ];

    return {
      ...regionData,
      pay_v2: {
        ...regionData.pay_v2,
        entities: combinedEntities,
      },
    };
  }, [regionData, countryData, needsDualRequest]);

  const geoData = useMemo(() => {
    if (!data?.pay_v2?.entities) return [];

    const metricField = (() => {
      if (metric === 'base_salary') return 'base';
      if (metric === 'total_compensation') return 'total';
      return 'hourly';
    })();

    // Group by geography ID and collect all values
    const geoMap = new Map<
      number,
      {
        id: number;
        label: string;
        values: number[];
        entityValues: Record<string, number>;
      }
    >();

    data.pay_v2.entities.forEach((entity) => {
      entity?.metrics?.top_geos?.forEach((item) => {
        const geoId = item.index ?? 0;
        const rawValue = item[metricField]?.[1] ?? 0;
        const value = exchangeRate ? rawValue * exchangeRate : rawValue;

        if (geoId > 0 && value > 0) {
          const entityName =
            entity.metadata?.short_name ||
            entity.metadata?.long_name ||
            'Unknown Entity';

          if (!geoMap.has(geoId)) {
            geoMap.set(geoId, {
              id: geoId,
              label: item.shortName ?? '',
              values: [],
              entityValues: {},
            });
          }

          const geo = geoMap.get(geoId);
          if (!geo) return;

          geo.values.push(value);
          geo.entityValues[entityName] = value;
        }
      });
    });

    // Calculate averages
    return Array.from(geoMap.values()).map((geo) => ({
      id: geo.id,
      label: geo.label,
      value: geo.values.reduce((sum, val) => sum + val, 0) / geo.values.length,
      tooltip: geo.entityValues,
    }));
  }, [data, metric, exchangeRate]);

  const granularitiesWithIds = extractGranularitiesWithIds(geoData);

  const { data: coords } =
    useCoordinateResolverForMixedGranularities(granularitiesWithIds);

  const points = useMemo((): MapPoint[] => {
    if (!coords.length || !geoData.length) return [];
    const coordMap = new Map(coords.map((c) => [c.id, c] as const));
    return geoData
      .map((d) => {
        const c = coordMap.get(d.id);
        if (!c) return null;
        const label = d.label || c.name;
        const locationType = determineLocationType(label, c.granularity);
        return {
          id: String(d.id),
          label,
          lat: c.lat,
          lon: c.lon,
          value: d.value,
          circleRadius: 0,
          locationType,
          ...(Object.keys(d.tooltip).length > 0 && { tooltip: d.tooltip }),
        };
      })
      .filter(
        (point): point is NonNullable<typeof point> => point !== null
      ) as MapPoint[];
  }, [coords, geoData]);

  if (!isPayMetric(metric as PayMetricValues)) {
    return <NoData />;
  }

  return children({ data: { points }, status });
};
